import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
// @ts-nocheck
import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import TradeModal from './TradeModal';
import StartTradeModal from './StartTradeModal';
import { pointsLabel } from '../../common/utils/pointsLabel';

/**
 * Trades dashboard — lists every trade the actor is a participant of, with
 * pending ones grouped at the top. Clicking a pending row reopens the
 * TradeModal pre-loaded with that trade id.
 *
 * History rows are read-only summaries (status, counterparty, points and
 * items exchanged). Polling is intentionally absent on this page — the
 * modal does its own polling when open; the list only refreshes on mount
 * and after a user-driven action. Keeps a "many idle users sitting on the
 * trades page" scenario from hammering the server.
 */
export default class TradesPage extends Page {
  constructor() {
    super(...arguments);
    _defineProperty(this, "loading", true);
    _defineProperty(this, "trades", []);
    _defineProperty(this, "err", '');
  }
  oninit(vnode) {
    super.oninit(vnode);
    if (!app.session.user) {
      m.route.set('/');
      return;
    }
    if (app.forum.attribute('pointSystemTradeEnabled') === false) {
      // Hard-gate: master toggle off → no page.
      m.route.set('/');
      return;
    }
    const title = app.translator.trans('ramon-point-system.forum.trades_page.title');
    app.history.push('trades', title);
    app.setTitle(title);
    this.load();
  }
  async load() {
    this.loading = true;
    m.redraw();
    try {
      const apiUrl = (app.forum.attribute('apiUrl') || '/api').replace(/\/+$/, '');
      const res = await app.request({
        method: 'GET',
        url: "".concat(apiUrl, "/point-system/trades")
      });
      this.trades = Array.isArray(res == null ? void 0 : res.data) ? res.data : [];
    } catch (e) {
      var _e$response;
      this.err = (e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) || 'load_failed';
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
  view() {
    if (this.loading) return m(LoadingIndicator, null);
    const t = (k, v) => app.translator.trans('ramon-point-system.forum.trades_page.' + k, v);

    /*
     * Dedup defensivo por trade.id antes de renderizar. O backend
     * (ListTradesController) filtra pending e history em queries com
     * status disjuntos, então a teoria diz "sem duplicata" — mas o
     * usuário reportou ver a mesma linha duas vezes no histórico
     * (2026-05-23). Sem um repro confiável, fechamos a hipótese mais
     * barata: garantir que IDs duplicados sumam aqui. Idempotente
     * caso o backend já entregue lista sem dups.
     */
    const seen = new Set();
    const tradesUnique = this.trades.filter(tr => {
      const id = Number(tr == null ? void 0 : tr.id);
      if (!Number.isFinite(id) || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    const pending = tradesUnique.filter(tr => tr.status === 'pending');
    const history = tradesUnique.filter(tr => tr.status !== 'pending');
    const canTrade = app.forum.attribute('pointSystemCanTrade') !== false;
    return m("div", {
      className: "PointSystemTradesPage container"
    }, m("div", {
      className: "PointSystemTradesPage-header"
    }, m("div", {
      className: "PointSystemTradesPage-header-titleRow"
    }, m("h1", null, m("i", {
      className: "fas fa-handshake"
    }), " ", t('title')), canTrade && m(Button, {
      className: "Button Button--primary",
      onclick: () => app.modal.show(StartTradeModal)
    }, m("i", {
      className: "fas fa-plus"
    }), " ", t('start_new'))), m("p", {
      className: "helpText"
    }, t('subtitle'))), !canTrade && m("p", {
      className: "PointSystemTradesPage-notice"
    }, m("i", {
      className: "fas fa-info-circle"
    }), " ", t('no_permission')), m("section", {
      className: "PointSystemTradesPage-section"
    }, m("h2", null, t('pending_heading')), pending.length === 0 ? m("p", {
      className: "PointSystemTradesPage-empty"
    }, t('pending_empty')) : m("ul", {
      className: "PointSystemTradesPage-list"
    }, pending.map(tr => this.renderRow(tr, true)))), m("section", {
      className: "PointSystemTradesPage-section"
    }, m("h2", null, t('history_heading')), history.length === 0 ? m("p", {
      className: "PointSystemTradesPage-empty"
    }, t('history_empty')) : m("ul", {
      className: "PointSystemTradesPage-list"
    }, history.map(tr => this.renderRow(tr, false)))));
  }
  renderRow(trade, isPending) {
    const t = (k, v) => app.translator.trans('ramon-point-system.forum.trades_page.' + k, v);
    const youAre = trade.youAre;
    const other = youAre === 'initiator' ? trade.recipient : trade.initiator;
    const yourItems = trade.items.filter(it => Number(it.ownerId) === Number(youAre === 'initiator' ? trade.initiator.id : trade.recipient.id));
    const theirItems = trade.items.filter(it => Number(it.ownerId) === Number(other == null ? void 0 : other.id));
    const yourPoints = youAre === 'initiator' ? trade.initiatorPoints : trade.recipientPoints;
    const theirPoints = youAre === 'initiator' ? trade.recipientPoints : trade.initiatorPoints;
    const accepted = youAre === 'initiator' ? trade.initiatorAccepted : trade.recipientAccepted;
    const theirAccepted = youAre === 'initiator' ? trade.recipientAccepted : trade.initiatorAccepted;
    const statusClass = "is-".concat(trade.status) + (isPending && accepted && !theirAccepted ? ' is-waiting' : '');
    return m("li", {
      className: "PointSystemTradesPage-row ".concat(statusClass),
      key: "trade-".concat(trade.id)
    }, m("div", {
      className: "PointSystemTradesPage-row-party"
    }, other != null && other.avatarUrl ? m("img", {
      className: "PointSystemTradesPage-row-avatar",
      src: other.avatarUrl,
      alt: ""
    }) : m("span", {
      className: "PointSystemTradesPage-row-avatar PointSystemTradesPage-row-avatar--placeholder",
      "aria-hidden": "true"
    }, m("i", {
      className: "fas fa-user"
    })), m("div", null, m("strong", null, (other == null ? void 0 : other.displayName) || (other == null ? void 0 : other.username) || '—'), m("div", {
      className: "PointSystemTradesPage-row-time"
    }, this.formatTime(trade.updatedAt)))), m("div", {
      className: "PointSystemTradesPage-row-summary"
    }, m("div", {
      className: "PointSystemTradesPage-row-side"
    }, m("span", {
      className: "muted"
    }, t('your_side')), m("span", null, yourPoints > 0 ? "".concat(Number(yourPoints).toLocaleString(), " ").concat(pointsLabel(app)) : '', ' ', yourItems.length > 0 ? "+ ".concat(t('items_short', {
      count: yourItems.length
    })) : '', yourPoints === 0 && yourItems.length === 0 ? m("em", null, "\u2014") : '')), m("i", {
      className: "fas fa-arrows-alt-h"
    }), m("div", {
      className: "PointSystemTradesPage-row-side"
    }, m("span", {
      className: "muted"
    }, t('their_side')), m("span", null, theirPoints > 0 ? "".concat(Number(theirPoints).toLocaleString(), " ").concat(pointsLabel(app)) : '', ' ', theirItems.length > 0 ? "+ ".concat(t('items_short', {
      count: theirItems.length
    })) : '', theirPoints === 0 && theirItems.length === 0 ? m("em", null, "\u2014") : ''))), m("div", {
      className: "PointSystemTradesPage-row-status"
    }, m("span", {
      className: "PointSystemTradesPage-pill ".concat(statusClass)
    }, t('status_' + trade.status)), isPending && m(Button, {
      className: "Button Button--primary",
      onclick: () => app.modal.show(TradeModal, {
        tradeId: trade.id
      })
    }, m("i", {
      className: "fas fa-arrow-right"
    }), " ", t('open'))));
  }
  formatTime(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString();
    } catch (_unused) {
      return '';
    }
  }
}
flarum.reg.add('ramon-point-system', 'forum/components/TradesPage', TradesPage);